import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Pager } from "@/components/common/States";
import { ReportView } from "@/components/ai/ReportView";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const KINDS = [["", "Tümü"], ["gunluk", "Günlük Özet"], ["vade", "Vade Raporu"]];

function ReportList({ items, active, onOpen }) {
  if (!items.length) return <div className="text-[12px] text-slate-400 p-3" data-testid="ai-list-empty">Henüz rapor yok</div>;
  return items.map((x) => (
    <button key={x.id} onClick={() => onOpen(x.id)} data-testid={`ai-report-item-${x.id}`}
      className={`w-full text-left p-3 border-b border-slate-100 transition-colors hover:bg-slate-50 ${active === x.id ? "bg-violet-50" : ""}`}>
      <div className="text-[13px] font-medium truncate">{x.title}</div>
      <div className="text-[11px] text-slate-500">{formatDate(x.created_at)} · {x.ai_status === "ok" ? "AI" : "yedek"}{x.trigger === "manuel" ? " · manuel" : ""}</div>
      {x.headline && <div className="text-[12px] text-slate-600 line-clamp-2 mt-0.5">{x.headline}</div>}
    </button>
  ));
}

export default function AiOzet() {
  const { can } = useAuth();
  const mgr = can("ai_ozet", "all");
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [cur, setCur] = useState(null);
  const [busy, setBusy] = useState("");
  const [email, setEmail] = useState(false);
  const id = params.get("id");
  const load = useCallback(() => api.get("/ai-ozet/reports", { params: { kind, page, page_size: 20 } }).then(({ data }) => setList(data)).catch((e) => toast.error(apiError(e))), [kind, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const target = id || list?.items?.[0]?.id;
    if (target) api.get(`/ai-ozet/reports/${target}`).then(({ data }) => setCur(data)).catch((e) => toast.error(apiError(e)));
  }, [id, list]);
  const gen = async (k) => {
    setBusy(k);
    try {
      const { data } = await api.post("/ai-ozet/generate", { kind: k, email });
      toast.success("Rapor oluşturuldu");
      setParams({ id: data.id });
      load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  return (
    <div data-testid="ai-ozet-page">
      <PageHeader title="AI Özetler" subtitle="Günlük Akıllı Özet ve Pazartesi Vade Raporu · sayılar ERP'den, yorumlar yapay zekâdan" testid="ai-ozet-title">
        <label className="flex items-center gap-2 text-[12px] text-slate-600"><Checkbox checked={email} onCheckedChange={(v) => setEmail(!!v)} data-testid="ai-generate-email" />E-posta da gönder</label>
        {mgr && <Button variant="outline" disabled={!!busy} onClick={() => gen("gunluk")} data-testid="ai-generate-gunluk">
          {busy === "gunluk" ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1 text-violet-600" />}Günlük özet oluştur</Button>}
        <Button className="bg-violet-600 hover:bg-violet-700" disabled={!!busy} onClick={() => gen("vade")} data-testid="ai-generate-vade">
          {busy === "vade" ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Vade raporu oluştur</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <div className="panel overflow-hidden self-start" data-testid="ai-report-list">
          <div className="flex gap-1 p-2 border-b border-slate-100">
            {KINDS.map(([k, l]) => <button key={k} onClick={() => { setKind(k); setPage(1); }} data-testid={`ai-filter-${k || "all"}`}
              className={`text-[12px] px-2.5 py-1 rounded-full transition-colors ${kind === k ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>)}
          </div>
          {!list ? <div className="h-40 animate-pulse" /> : <ReportList items={list.items} active={cur?.id} onOpen={(i) => setParams({ id: i })} />}
          {list && <div className="p-2"><Pager page={page} pageSize={20} total={list.total} onPage={setPage} testid="ai-pager" /></div>}
        </div>
        <div>{cur ? <ReportView r={cur} /> : <div className="panel p-8 text-center text-slate-400 text-sm" data-testid="ai-empty">Rapor seçin veya oluşturun</div>}</div>
      </div>
    </div>
  );
}
